import { useEffect, useMemo } from 'react';
import { create } from 'zustand';
import { useAuthStore } from '@/stores/authStore';
import { GameStateTransport, type GameStateConnectionConfig, type GameStateConnectionStatus,
  type GameStateSocket } from '@/lib/gameState/transport';
import { parseGameIdentity, type AdmittedGameState, type GameStateComparison } from '@/lib/gameState/admission';
import { emptyGameStateDiagnostics, GameStateDiagnosticJournal, type GameStateDiagnostics } from '@/lib/gameState/diagnostics';

/** The override is a base URL, not the legacy analytics base. An absent
 * override leaves legacy scores/consensus alone and opens no Core socket.
 * Never borrow the analytics bearer for an explicitly configured Core.
 */
export function gameStateConfig(env: Record<string, unknown>): GameStateConnectionConfig | null {
  const baseUrl = typeof env.VITE_HYDRA_GAMESTATE_URL === 'string' ? env.VITE_HYDRA_GAMESTATE_URL.trim() : '';
  if (!baseUrl) return null;
  const token = [env.VITE_HYDRA_GAMESTATE_TOKEN, env.VITE_HYDRA_CORE_TOKEN]
    .find(value => typeof value === 'string' && value.trim()) as string | undefined;
  return { baseUrl, token: token?.trim() ?? '' };
}
const config = gameStateConfig(import.meta.env);

interface CoreGameStatesView {
  configured: boolean;
  status: GameStateConnectionStatus;
  error: string | null;
  records: ReadonlyMap<string, AdmittedGameState>;
  comparisons: ReadonlyMap<string, GameStateComparison>;
  diagnostics: GameStateDiagnostics;
}
export const useCoreGameStatesStore = create<CoreGameStatesView>(() => ({
  configured: config !== null, status: 'stopped', error: null, records: new Map(), comparisons: new Map(),
  diagnostics: emptyGameStateDiagnostics(),
}));

let journal = new GameStateDiagnosticJournal();
let diagnosticScope = currentAuthScope();
// History must also clear when auth changes after the last comparison reader
// unmounted; the transport's shorter-lived auth subscription cannot do that.
const removeDiagnosticAuth = useAuthStore.subscribe(() => {
  const scope = currentAuthScope();
  if (scope === diagnosticScope) return;
  diagnosticScope = scope;
  journal = new GameStateDiagnosticJournal();
  useCoreGameStatesStore.setState({ diagnostics: emptyGameStateDiagnostics() });
});
if (import.meta.hot) import.meta.hot.dispose(removeDiagnosticAuth);

const interests = new Map<symbol, readonly string[]>();
const interestedKeys = () => [...new Set([...interests.values()].flat())].sort();
let owner: GameStateTransport | null = null;
let authScope: string | null = null;
let removeVisibility: (() => void) | null = null;
let removeAuth: (() => void) | null = null;

function clearOwner(): void {
  owner?.stop();
  owner = null;
  removeVisibility?.(); removeVisibility = null;
  removeAuth?.(); removeAuth = null;
  authScope = null;
  useCoreGameStatesStore.setState({ status: 'stopped', error: null, records: new Map(), comparisons: new Map(),
    diagnostics: journal.observe('stopped', new Map(), new Map(), Date.now()) });
}

function socketFor(url: string): GameStateSocket {
  const ws = new WebSocket(url);
  const socket: GameStateSocket = { onopen: null, onmessage: null, onerror: null, onclose: null,
    send: data => ws.send(data), close: () => ws.close() };
  ws.onopen = () => socket.onopen?.();
  ws.onmessage = event => socket.onmessage?.({ data: event.data });
  ws.onerror = () => socket.onerror?.();
  ws.onclose = () => socket.onclose?.();
  return socket;
}

function currentAuthScope(): string | null {
  const auth = useAuthStore.getState();
  return auth.status === 'authenticated' && auth.user
    ? JSON.stringify([auth.user.id, auth.user.orgId, auth.user.role]) : null;
}

function reconcile(): void {
  const nextScope = currentAuthScope();
  if (!config || !interests.size || !nextScope) { clearOwner(); return; }
  if (owner && authScope === nextScope) { owner.setKeys(interestedKeys()); return; }
  clearOwner();
  authScope = nextScope;
  try {
    owner = new GameStateTransport({ config, keys: interestedKeys(), socket: socketFor,
      read: async (url, init) => {
        const response = await fetch(url, init);
        if (!response.ok) throw new Error('GameState comparison unavailable');
        return response.json();
      },
      visible: () => !document.hidden,
      changed: transport => {
        if (currentAuthScope() !== nextScope || authScope !== nextScope) return;
        const records = transport.admission.snapshot();
        const comparisons = transport.admission.comparisons();
        useCoreGameStatesStore.setState({ status: transport.status, error: transport.error,
          records, comparisons, diagnostics: journal.observe(transport.status, records, comparisons, Date.now()) });
      },
    });
    const onVisibility = () => { if (!document.hidden) owner?.refresh(); };
    document.addEventListener('visibilitychange', onVisibility);
    removeVisibility = () => document.removeEventListener('visibilitychange', onVisibility);
    removeAuth = useAuthStore.subscribe(() => {
      if (currentAuthScope() !== authScope) reconcile();
    });
    owner.start();
  } catch {
    clearOwner();
    useCoreGameStatesStore.setState({ status: 'disconnected', error: 'GameState configuration unavailable',
      diagnostics: journal.observe('disconnected', new Map(), new Map(), Date.now()) });
  }
}

/** One gamestate-only socket and one unioned batch-poll owner per application,
 * independent of the market feed's direct/SharedWorker mode. No per-game
 * transport. Removing the final reader tears down all timers and listeners.
 */
export function useCoreGameStateInterest(key: string | null): void {
  useCoreGameStateInterests(key ? [key] : []);
}

/** Dashboard lists and detail panels contribute to the same union. Stable
 * key content avoids tearing down a reader on an ordinary parent render. */
export function useCoreGameStateInterests(keys: readonly string[]): void {
  const inputKeys = JSON.stringify(keys);
  const encodedKeys = useMemo(() => {
    if (!config) return '[]';
    const candidates: string[] = JSON.parse(inputKeys);
    return JSON.stringify([...new Set(candidates.filter(key => parseGameIdentity(key)))].sort());
  }, [inputKeys]);
  const auth = useAuthStore(s => s.status === 'authenticated' ? s.user : null);
  const scope = auth ? JSON.stringify([auth.id, auth.orgId, auth.role]) : null;
  useEffect(() => {
    const admittedKeys: string[] = JSON.parse(encodedKeys);
    if (!config || !scope || !admittedKeys.length) return;
    const id = Symbol();
    interests.set(id, admittedKeys);
    reconcile();
    return () => { interests.delete(id); reconcile(); };
  }, [encodedKeys, scope]);
}
